import type { StockyPOResults } from '@/models/stocky_migration';

export interface StockyPOStatTile {
  key: string;
  label: string;
  value: number;
  icon: string;
  color: string;
  bg: string;
}

/**
 * Presentation logic for the Stocky PO migration completion screen.
 *
 * Kept out of the .vue file so it can be tested directly rather than through a
 * mirrored copy of the component.
 */

/** Fill in every counter, so callers never have to guard each field. */
export function normalizePOResults(raw: Partial<StockyPOResults> | undefined | null): StockyPOResults {
  const r = raw || {};
  return {
    created: r.created || 0,
    updated: r.updated || 0,
    skippedLocked: r.skippedLocked || 0,
    overwrittenLocked: r.overwrittenLocked || 0,
    discardedReturns: r.discardedReturns || 0,
    discardedAdjustments: r.discardedAdjustments || 0,
    discardedSyncedReceipts: r.discardedSyncedReceipts || 0,
    skipped: r.skipped || 0,
    failed: r.failed || 0,
    totalItems: r.totalItems || 0,
    matchedItems: r.matchedItems || 0,
    unmatchedItems: r.unmatchedItems || 0,
    totalStockyPOs: r.totalStockyPOs || 0,
    orphansRecovered: r.orphansRecovered || 0,
    unreconciled: r.unreconciled || 0,
    preExistingDuplicates: r.preExistingDuplicates || 0,
    renamedOnNumberCollision: r.renamedOnNumberCollision || 0,
    paginationSuspect: r.paginationSuspect || false,
    suppliersUnresolved: r.suppliersUnresolved || 0,
    errors: r.errors || [],
  };
}

/**
 * Headline tiles.
 *
 * `updated` and `skippedLocked` are the two that matter on a re-run. Before they
 * were shown, a re-run against an already-migrated store reported "0 created"
 * while quietly refreshing hundreds of POs, so a successful run looked like a
 * no-op.
 */
export function buildStatTiles(r: StockyPOResults): StockyPOStatTile[] {
  return [
    { key: 'created', label: 'POs Created', value: r.created, icon: 'mdi-plus-circle', color: 'success', bg: 'green lighten-5' },
    { key: 'updated', label: 'POs Updated', value: r.updated, icon: 'mdi-refresh-circle', color: 'primary', bg: 'blue lighten-5' },
    // One slot, two mutually exclusive outcomes: a run either protects locked
    // POs or replaces them. Showing the protected tile at 0 after an overwrite
    // run would hide the number that actually mattered.
    //
    // "Incl." because these POs are also inside the Updated tile beside it —
    // a bare "Replaced" invites reading the row as a total.
    r.overwrittenLocked > 0
      ? { key: 'overwritten', label: 'Incl. Replaced', value: r.overwrittenLocked, icon: 'mdi-swap-horizontal-bold', color: 'warning', bg: 'orange lighten-5' }
      : { key: 'protected', label: 'Not Refreshed', value: r.skippedLocked, icon: 'mdi-shield-check', color: 'teal', bg: 'teal lighten-5' },
    {
      key: 'failed',
      label: 'POs Failed',
      value: r.failed,
      icon: 'mdi-close-circle',
      color: r.failed > 0 ? 'error' : 'grey',
      bg: r.failed > 0 ? 'red lighten-5' : 'grey lighten-4',
    },
  ];
}

export function completionHeadline(r: StockyPOResults): string {
  // Failures first. This demotes a run that created 500 and failed 1, but a
  // failure the merchant never notices is the worse outcome.
  if (r.failed > 0) {
    return 'Migration Completed with Errors';
  }
  // Protected-only: POs were found, and deliberately left alone. Saying
  // "Nothing to Import" here would claim Stocky had nothing to offer.
  if (r.created === 0 && r.updated === 0 && r.skippedLocked > 0) {
    return 'Purchase Orders Left Unchanged';
  }
  if (r.created === 0 && r.updated === 0) {
    return 'Nothing to Import';
  }
  if (r.created === 0) {
    return 'Purchase Orders Updated';
  }
  return 'Migration Complete';
}

/** Says what actually happened, rather than always claiming a fresh import. */
export function completionSubtitle(r: StockyPOResults): string {
  const parts: string[] = [];
  if (r.created > 0) {
    parts.push(`${r.created} imported`);
  }
  // The backend counts an overwrite as an update first, so overwrittenLocked is
  // always a subset of updated. Listing them as two clauses read as two disjoint
  // groups — "42 refreshed, 7 replaced" for 42 POs — so the smaller count is
  // nested inside the larger one instead.
  if (r.updated > 0) {
    parts.push(r.overwrittenLocked > 0
      ? `${r.updated} refreshed from Stocky (${r.overwrittenLocked} replaced)`
      : `${r.updated} refreshed from Stocky`);
  }
  if (r.skippedLocked > 0) {
    parts.push(`${r.skippedLocked} left untouched`);
  }
  if (r.failed > 0) {
    parts.push(`${r.failed} failed`);
  }
  // Only reachable when nothing at all was found — without the `failed` clause
  // above, a run where every PO failed would have claimed nothing matched.
  if (parts.length === 0) {
    return 'No purchase orders matched the selected statuses.';
  }
  return `${parts.join(', ')}.`;
}

/**
 * Signals that the import may not reflect all of Stocky. Not failures, but a
 * merchant should not treat the result as complete without seeing them.
 */
export function dataWarnings(r: StockyPOResults): string[] {
  const warnings: string[] = [];
  if (r.paginationSuspect) {
    warnings.push('Stocky returned repeated pages, so some purchase orders may not have been fetched. Re-run the import to pick up anything missed.');
  }
  if (r.unreconciled > 0) {
    warnings.push(`${r.unreconciled} purchase order(s) reported by Stocky could not be accounted for in this run.`);
  }
  if (r.preExistingDuplicates > 0) {
    warnings.push(`${r.preExistingDuplicates} duplicate purchase order(s) from an earlier import are still in your account. Contact support to have them cleaned up.`);
  }
  // Silent before this: the merchant saw "PO-1001" and "PO-1001-stocky" side by
  // side with nothing explaining where the second one came from.
  if (r.renamedOnNumberCollision > 0) {
    warnings.push(`${r.renamedOnNumberCollision} purchase order(s) from Stocky had the same number as a purchase order already in Assisty, so they were imported with a "-stocky" suffix. They are separate purchase orders, not duplicates.`);
  }
  if (r.suppliersUnresolved > 0) {
    warnings.push(`${r.suppliersUnresolved} purchase order(s) could not be matched to an Assisty supplier and were imported without one.`);
  }
  // Overwrite losses are irreversible, so they are stated as what was destroyed
  // rather than folded into a count of what changed.
  if (r.overwrittenLocked > 0) {
    warnings.push(`${r.overwrittenLocked} purchase order(s) you had received or edited in Assisty were replaced with Stocky's version.`);
  }
  if (r.discardedReturns > 0 || r.discardedAdjustments > 0) {
    warnings.push(`${r.discardedReturns} return(s) and ${r.discardedAdjustments} adjustment(s) were deleted. These existed only in Assisty and cannot be restored by re-importing.`);
  }
  if (r.discardedSyncedReceipts > 0) {
    warnings.push(`${r.discardedSyncedReceipts} deleted receipt(s) had already updated stock in Shopify. Those stock changes remain in Shopify — check the on-hand quantities for those products.`);
  }
  return warnings;
}

/**
 * `errors` is a mixed bag: reconciliation summaries, per-PO failures,
 * supplier-unresolved notices, and "not refreshed because ..." protection notes
 * all land in the same array. Its length is therefore not an error count — a run
 * with a single reconciliation note and no failures would have been labelled an
 * error.
 *
 * So take the failure count from `failed`, which is authoritative, and describe
 * the array as what it is: details. Deliberately not partitioning by message
 * text — that would couple this label to backend wording.
 */
export function detailPanelLabel(r: StockyPOResults): string {
  const n = r.errors.length;
  return r.failed > 0
    ? `${r.failed} failed purchase order(s) — ${n} import detail(s)`
    : `${n} note(s) about this import`;
}

export function detailPanelIsError(r: StockyPOResults): boolean {
  return r.failed > 0;
}
